import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { usePlayerProgression } from "@/hooks/usePlayerProgression";
import { useStreaks } from "@/hooks/useStreaks";
import { useMissions } from "@/hooks/useMissions";
import DailyCheckInModal from "@/components/DailyCheckInModal";
import OnboardingOverlay from "@/components/dashboard/OnboardingOverlay";
import PostOnboardingModal from "@/components/dashboard/PostOnboardingModal";
import DashboardSidebar from "@/components/dashboard/DashboardSidebar";
import InstructorHeroBanner from "@/components/dashboard/InstructorHeroBanner";
import ContinueLearningRow from "@/components/dashboard/ContinueLearningRow";
import ProgressStatsRow from "@/components/dashboard/ProgressStatsRow";
import LaunchPathCard from "@/components/dashboard/LaunchPathCard";
import EngineStatusCard from "@/components/learning-engine/EngineStatusCard";
import { useHousehold } from "@/hooks/useHousehold";
import { useHouseholdRole } from "@/hooks/useHouseholdRole";
import { useProfile } from "@/hooks/useProfile";
import { useCareerActivation } from "@/hooks/useCareerActivation";
import { useDailyPaycheck, type DailyPaycheck } from "@/hooks/useDailyPaycheck";
import DailyPaycheckModal from "@/components/career/DailyPaycheckModal";
import SEO from "@/components/SEO";
import { isKnownParentEmail } from "@/lib/qaAccounts";
import type { AnyRow } from "@/lib/rowTypes";
import SeatOnboardingFlow from "@/components/onboarding/SeatOnboardingFlow";
import { useLearnerSeatCutover } from "@/hooks/useLearnerSeatCutover";
import { useLearnerSeats } from "@/hooks/useLearnerSeats";

type OnboardingDiagnostic = {
  code?: string;
  message: string;
  details?: string;
  hint?: string;
  context: string;
};

const isDiagnosticsSafeHost = () => {
  if (import.meta.env.DEV) return true;
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host.endsWith(".lovable.app");
};

const normalizeOnboardingError = (error: AnyRow, context: string): OnboardingDiagnostic => {
  const maybeError = error as Partial<{ code: string; message: string; details: string; hint: string }> | null;
  return {
    code: maybeError?.code,
    message: maybeError?.message || (error instanceof Error ? error.message : "Unknown onboarding failure"),
    details: maybeError?.details,
    hint: maybeError?.hint,
    context: (maybeError as Partial<OnboardingDiagnostic> | null)?.context || context,
  };
};

const Dashboard = () => {
  const { user } = useAuth();
  const { refetch: refetchLevel } = usePlayerProgression();
  const { loading: streaksLoading, checkInDaily, checkInResult, clearCheckInResult } = useStreaks();
  const [showCheckIn, setShowCheckIn] = useState(false);
  const { activated: careerActivated } = useCareerActivation();
  const { claim: claimPaycheck, allocate: allocatePaycheck } = useDailyPaycheck();
  const [pendingPaycheck, setPendingPaycheck] = useState<DailyPaycheck | null>(null);
  const { assignMissions } = useMissions();
  const { profile, complete: profileComplete, loading: profileLoading, refetch: refetchProfile } = useProfile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role: householdRole, loading: roleLoading } = useHouseholdRole();
  const { isParent: householdParent, loading: householdLoading, household } = useHousehold();
  const { enabled: seatCutover, loading: cutoverLoading } = useLearnerSeatCutover();
  const isHouseholdOwner = !!household && household.owner_id === user?.id;
  const { data: seatData, isLoading: seatsLoading, isError: seatsError, refetch: refetchSeats } = useLearnerSeats(seatCutover && isHouseholdOwner ? household?.id : null);
  // Avoid flashing the legacy (child-required) onboarding or defaulting a Family
  // owner to 1-seat choices while cutover / household / seat data is loading.
  const seatOnboardingPending =
    cutoverLoading || (seatCutover && (householdLoading || (isHouseholdOwner && seatsLoading)));
  const isKnownParentQa = isKnownParentEmail(user?.email);

  // Parents land on the Parent Hub by default at sign-in (handled in Login),
  // but they can freely navigate into the learner Home/Beta experience from
  // the sidebar — do NOT force-redirect them away from /dashboard here.

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPostOnboarding, setShowPostOnboarding] = useState(false);

  const completeOnboarding = async () => {
    if (!user) return;

    const emailBase =
      (user.email?.split("@")[0] ?? "learner")
        .replace(/[^a-zA-Z0-9_]/g, "")
        .slice(0, 32) || "learner";

    const payload: AnyRow = {
      id: user.id,
      onboarding_completed: true,
      onboarding_completed_at: new Date().toISOString(),
    };

    // Display name is a convenience/default only. Avatar and username are
    // social identity fields and are intentionally not required for dashboard.
    if (!(profile)?.display_name?.trim()) {
      payload.display_name = emailBase;
    }

    const { error } = await supabase.from("profiles").upsert(payload, { onConflict: "id" });
    if (error) throw normalizeOnboardingError(error, "profiles.upsert:onboarding_complete");
    await refetchProfile();
  };

  // Handle billing return from Stripe checkout
  useEffect(() => {
    const billingStatus = searchParams.get("billing");
    if (billingStatus === "success") {
      toast.success("Welcome to HeirWise! Your subscription is active.");
      setSearchParams({}, { replace: true });
    } else if (billingStatus === "cancelled") {
      toast.info("Checkout was cancelled. You can subscribe anytime.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!user || profileLoading) return;
    const key = `heirwise_onboarding_${user.id}`;
    const postKey = `heirwise_post_onboarding_${user.id}`;

    // Server-side truth wins: a returning user with onboarding_completed=true
    // must never be routed back into onboarding, identity, avatar, username,
    // friends, or challenge setup.
    if (profile?.onboarding_completed === true) {
      setShowOnboarding(false);
      if (!localStorage.getItem(postKey)) setShowPostOnboarding(true);
      return;
    }

    // Legacy migration fallback: old users may have completed the prior local
    // onboarding flow before the server flag existed. Backfill the server flag
    // only when that local completion marker is present; do not let optional
    // social identity fields silently bypass onboarding for brand-new users.
    if (profileComplete && localStorage.getItem(key)) {
      setShowOnboarding(false);
      void completeOnboarding();
      if (!localStorage.getItem(postKey)) setShowPostOnboarding(true);
      return;
    }

    // Only truly new/incomplete users see the simple product onboarding.
    // Do NOT fall through to OnboardingProfileSetup; that screen belongs to
    // optional social/profile settings, not core beta access.
    if (!localStorage.getItem(key)) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [user, profileLoading, profileComplete, profile?.onboarding_completed]);

  const handleOnboardingComplete = async () => {
    if (!user) return;
    try {
      const isParent = householdRole === "parent" || householdParent || isKnownParentQa;

      // Parent exception: parents are not marked complete from the learner
      // dashboard tour unless they already have at least one child profile or
      // household child member. If they still need family setup, route them to
      // the Parent Hub instead of the social identity screen.
      if (isParent) {
        const [householdChildren, childProfiles] = await Promise.all([
          supabase
            .from("household_members")
            .select("id, households!inner(owner_id)", { count: "exact", head: true })
            .eq("role", "child")
            .eq("households.owner_id", user.id),
          supabase
            .from("child_profiles")
            .select("id", { count: "exact", head: true })
            .eq("parent_user_id", user.id),
        ]);

        if (householdChildren.error) {
          throw normalizeOnboardingError(householdChildren.error, "household_members.select:parent_child_count");
        }
        if (childProfiles.error) {
          throw normalizeOnboardingError(childProfiles.error, "child_profiles.select:parent_child_count");
        }

        const householdChildCount = householdChildren.count;
        const childProfileCount = childProfiles.count;

        if ((householdChildCount ?? 0) === 0 && (childProfileCount ?? 0) === 0) {
          setShowOnboarding(false);
          navigate("/parent-dashboard", { replace: true });
          return;
        }
      }

      await completeOnboarding();
      localStorage.setItem(`heirwise_onboarding_${user.id}`, "true");
      setShowOnboarding(false);
      setShowPostOnboarding(true);
    } catch (error) {
      const diagnostic = normalizeOnboardingError(error, "dashboard.handleOnboardingComplete");
      if (isDiagnosticsSafeHost()) {
        console.error("[onboarding-complete] failed", diagnostic);
        toast.error(
          `Unable to finish onboarding (${diagnostic.context}${diagnostic.code ? ` · ${diagnostic.code}` : ""}): ${diagnostic.message}`,
        );
      } else {
        toast.error("Unable to finish onboarding. Please try again.");
      }
    }
  };

  // After streak check-in modal closes, surface the daily paycheck (idempotent server-side).
  const handleCheckInDismiss = async () => {
    setShowCheckIn(false);
    clearCheckInResult();
    if (!careerActivated) return;
    try {
      const pc = await claimPaycheck();
      if (pc && pc.status === "pending_allocation") {
        setPendingPaycheck(pc);
      }
    } catch (err) {
      // silent — paycheck is best-effort
      console.warn("[paycheck] claim failed", err);
    }
  };

  const handlePostOnboardingClose = () => {
    if (user) localStorage.setItem(`heirwise_post_onboarding_${user.id}`, "true");
    setShowPostOnboarding(false);
  };

  useEffect(() => {
    if (!user || streaksLoading) return;
    const doCheckIn = async () => {
      const result = await checkInDaily();
      if (result && !result.already_claimed) {
        setShowCheckIn(true);
        refetchLevel();
      }
      await assignMissions();
    };
    void doCheckIn();
  }, [user, streaksLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstName =
    (profile)?.display_name?.split(" ")[0] ||
    user?.email?.split("@")[0] ||
    "Learner";

  if (roleLoading || householdLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-5 h-5 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex w-full">
      <SEO
        title="Dashboard — HeirWise"
        description="Your HeirWise command center: track learning progress, daily missions, balances, and your next Economic & Enterprise Education step."
        path="/dashboard"
      />
      <DashboardSidebar />

      <div className="flex-1 min-w-0">
        <main className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-8 max-w-6xl mx-auto pt-16 lg:pt-10">
          {/* Welcome */}
          <div className="space-y-1">
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-foreground">
              Welcome back, {firstName} <span className="text-primary">✦</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              Your journey to financial mastery continues here.
            </p>
          </div>

          <InstructorHeroBanner />

          <EngineStatusCard />

          <LaunchPathCard />

          <ContinueLearningRow />

          <ProgressStatsRow />
        </main>
      </div>

      {showOnboarding && seatOnboardingPending ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95" role="status" aria-live="polite">
          <p className="text-sm text-muted-foreground">Preparing your setup…</p>
        </div>
      ) : showOnboarding && seatCutover && isHouseholdOwner && (seatsError || !seatData) ? (
        <div className="fixed inset-0 z-[200] flex flex-col items-center justify-center gap-3 bg-background/95 px-4 text-center">
          <p role="alert" className="text-sm text-destructive">We couldn't load your learner seats. Please try again.</p>
          <button type="button" onClick={() => void refetchSeats()} className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">Try again</button>
        </div>
      ) : showOnboarding && seatCutover && isHouseholdOwner && user && household && seatData ? (
        <SeatOnboardingFlow
          userId={user.id}
          householdId={household.id}
          maxLearners={seatData.summary.max}
          profile={{ display_name: profile?.display_name ?? null, username: (profile as AnyRow | null)?.username ?? null }}
          onComplete={() => {
            localStorage.setItem(`heirwise_onboarding_${user.id}`, "true");
            localStorage.setItem(`heirwise_post_onboarding_${user.id}`, "true");
            setShowOnboarding(false);
            void refetchProfile();
          }}
        />
      ) : showOnboarding ? (
        <OnboardingOverlay onComplete={handleOnboardingComplete} />
      ) : null}
      {!showOnboarding && showPostOnboarding && (
        <PostOnboardingModal onClose={handlePostOnboardingClose} />
      )}

      {showCheckIn && checkInResult && (
        <DailyCheckInModal
          result={checkInResult}
          onDismiss={handleCheckInDismiss}
        />
      )}

      {pendingPaycheck && (
        <DailyPaycheckModal
          paycheck={pendingPaycheck}
          onAllocate={async (alloc) => { await allocatePaycheck(pendingPaycheck.id, alloc); }}
          onClose={() => setPendingPaycheck(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;
